import { EhrData, EhrTypeOfWork } from 'chaika-frontend-types/frontend-cms';
import { OptionType } from '@components/Select';
import { EhrIdentityDocType, RepresentativeType } from 'chaika-enums';
import { getEnumValues } from '@utils/getEnumValues';
import { translate } from '@chaika/i18n/helper';
import { getRepresentativeTypeTranslation } from '@utils/enumTranslation';

type EhrFieldWithName = keyof Omit<
    EhrData,
    | 'id'
    | 'recordId'
    | 'isDocumentsSigned'
    | 'creationTime'
    | 'isPersonalAreaCreated'
    | 'mergedEhrIds'
    | 'legacyData'
    | 'contactPersons'
    | 'isBlocked'
    | 'blockedBy'
    | 'translations'
>;

export const EHR_FIELD_NAMES: { [key in EhrFieldWithName]: () => string } = {
    firstName: () => translate('name'),
    lastName: () => translate('lastName'),
    oldLastName: () => translate('oldLastName'),
    patronymic: () => translate('patronymic'),
    mainEhrId: () => 'mainEhrId',
    engFirstName: () => translate('nameInEnglish'),
    engLastName: () => translate('lastNameInEnglish'),
    engPatronymic: () => translate('patronymicInEnglish'),
    telegram: () => 'telegram',
    telegramName: () => 'telegramName',
    skype: () => 'skype',
    viber: () => 'viber',
    facebook: () => 'facebook',
    whatsApp: () => 'whatsApp',
    primaryChannel: () => translate('notificationMethod'),
    birthDate: () => translate('birthDate'),
    email: () => 'E-mail',
    sex: () => translate('sex'),
    snils: () => translate('SNILS'),
    inn: () => translate('INN'),
    phone: () => translate('phone'),
    identityDoc: () => translate('seriesDocumentNumber'),
    identityDocType: () => translate('documentType'),
    identityDocDate: () => translate('dateOfIssue'),
    identityDocIssuer: () => translate('whoIssued'),
    identityDocIssuerTransliteration: () => `${translate('whoIssued')} ${translate('translit')}`,
    identityDocCode: () => translate('departmentCode'),
    internationalIdNumber: () => translate('foreignPassportNumber'),
    internationalIdIssuingStateCode: () => translate('issuingStateCode'),
    internationalIdIssueDate: () => translate('dateOfIssue'),
    internationalIdExpiryDate: () => translate('expirationDateLongText'),
    internationalIdAuthority: () => translate('whoIssued'),
    internationalIdAuthorityTransliteration: () => `${translate('whoIssued')} ${translate('translit')}`,
    registrationAddress: () => translate('registrationAddress'),
    registrationAddressTransliteration: () => `${translate('registrationAddress')} ${translate('translit')}`,
    legalDocConfirm: () => translate('passportDetailsConfirmed'),
    extraDocConfirm: () => translate('additionalDataConfirmed'),
    address: () => translate('actualAddress'),
    addressTransliteration: () => `${translate('actualAddress')} ${translate('translit')}`,
    typeOfWork: () => translate('occupation'),
    placeOfWork: () => translate('placeOfWork'),
    comment: () => translate('comment'),
    paymentComment: () => translate('commentsOnPayments'),
    blockComment: () => translate('commentOnBlocking'),
};

export function getPlaceOfWorkTitle(typeOfWork?: EhrTypeOfWork): string {
    let title = '';

    switch (typeOfWork) {
        case EhrTypeOfWork.Job:
            title = translate('placeOfWork');
            break;
        case EhrTypeOfWork.Study:
            title = translate('placeOfStudy');
            break;
        default:
            title = translate('descriptionOfActivityType');
    }

    return title;
}

export const typeOfWorkOptions = (): OptionType<EhrTypeOfWork>[] => [
    {
        value: EhrTypeOfWork.Job,
        label: translate('job'),
    },
    {
        value: EhrTypeOfWork.Study,
        label: translate('study'),
    },
    {
        value: EhrTypeOfWork.Other,
        label: translate('other'),
    },
];

export const identityDocTypeOptions = (): OptionType<EhrIdentityDocType>[] => [
    {
        value: EhrIdentityDocType.RussianPassport,
        label: translate('russianPassport'),
    },
    {
        value: EhrIdentityDocType.ForeignPassport,
        label: translate('foreignPassport'),
    },
];

export const representativeTypeOption = (): OptionType<RepresentativeType>[] =>
    getEnumValues(RepresentativeType).map(value => ({
        value,
        label: getRepresentativeTypeTranslation(value),
    }));
